<script setup lang="ts">
import { onMounted, onUnmounted, nextTick, ref } from 'vue';
import { SVG, Svg } from '@svgdotjs/svg.js';
import { Bezier } from "bezier-js";
import TechnologyItem from '@/components/Sections/Research/TechnologyItem.vue';
import PrototypeItem from '@/components/Sections/Research/PrototypeItem.vue';
import { useAssetStore } from '@/stores/assetStore.ts';
import { useGameStore } from '@/stores/gameStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { logger } from '@/models/logger';

/*
 * Stores / models
 */
const assetStore = useAssetStore();
const gameStore = useGameStore();
const svgContainer = ref<Svg>();

/*
 * Variables
 */
const isDragging = ref(false);
const startX = ref(0);
const startY = ref(0);
const scrollLeft = ref(0);
const scrollTop = ref(0);
const zoomLevel = ref(1);
const minZoom = 0.5;
const maxZoom = 1;
const zoomStep = 0.1;

let containerElement: HTMLElement | null = null;
let contentElement: HTMLElement | null = null;
let resizeObserver: ResizeObserver | null = null;

// Mouse event handlers for dragging functionality
function onMouseDown(event: MouseEvent) {
  isDragging.value = true;
  startX.value = event.pageX - containerElement!.offsetLeft;
  startY.value = event.pageY - containerElement!.offsetTop;
  scrollLeft.value = containerElement!.scrollLeft;
  scrollTop.value = containerElement!.scrollTop;
  containerElement!.classList.add('grabbing'); // Add grabbing class
}

function onMouseMove(event: MouseEvent) {
  if (!isDragging.value) return
  event.preventDefault()

  const x = event.pageX - containerElement!.offsetLeft;
  const y = event.pageY - containerElement!.offsetTop;
  const walkX = (x - startX.value); // Multiply by 1 for faster scrolling
  const walkY = (y - startY.value);

  const newScrollLeft = scrollLeft.value - walkX;
  const newScrollTop = scrollTop.value - walkY;
  
  // Limit scrolling to content bounds
  limitScroll(newScrollLeft, newScrollTop);
}

function onMouseUp() {
  isDragging.value = false;
  containerElement!.classList.remove('grabbing'); // Remove grabbing class
}

// Helper function to limit scrolling to content bounds
function limitScroll(scrollLeft?: number, scrollTop?: number) {
  if (!containerElement || !contentElement) return;
  
  const containerWidth = containerElement.clientWidth;
  const containerHeight = containerElement.clientHeight;
  const contentWidth = contentElement.offsetWidth * zoomLevel.value;
  const contentHeight = contentElement.offsetHeight * zoomLevel.value;
  
  // Calculate max scroll values
  const maxScrollLeft = Math.max(0, contentWidth - containerWidth);
  const maxScrollTop = Math.max(0, contentHeight - containerHeight);
  
  // Apply scroll limits
  if (scrollLeft !== undefined) {
    containerElement.scrollLeft = Math.max(0, Math.min(maxScrollLeft, scrollLeft));
  }
  if (scrollTop !== undefined) {
    containerElement.scrollTop = Math.max(0, Math.min(maxScrollTop, scrollTop));
  }
}

// Wheel event handler for zooming
function onWheel(event: WheelEvent) {
  // Only handle wheel events on desktop (not touch devices)
  if ('ontouchstart' in window) return;
  
  event.preventDefault();
  
  // Get the mouse position relative to the container
  const rect = containerElement!.getBoundingClientRect();
  const mouseX = event.clientX - rect.left;
  const mouseY = event.clientY - rect.top;
  
  // Calculate the position of the mouse in the unscaled content
  const scrollX = containerElement!.scrollLeft;
  const scrollY = containerElement!.scrollTop;
  const contentX = (scrollX + mouseX) / zoomLevel.value;
  const contentY = (scrollY + mouseY) / zoomLevel.value;
  
  // Update zoom level
  const delta = event.deltaY > 0 ? -zoomStep : zoomStep;
  const newZoom = Math.max(minZoom, Math.min(maxZoom, zoomLevel.value + delta));
  
  if (newZoom !== zoomLevel.value) {
    zoomLevel.value = newZoom;
    
    // Recalculate scroll position to keep the mouse position fixed
    nextTick(() => {
      const newScrollX = contentX * newZoom - mouseX;
      const newScrollY = contentY * newZoom - mouseY;
      
      limitScroll(newScrollX, newScrollY);
    });
  }
}

// SVG line drawing functionality
function drawLines() {
  if (!svgContainer.value || !contentElement) return

  svgContainer.value.clear(); // Clear previous lines

  const parentBox = contentElement.getBoundingClientRect();



  let minX = Infinity, minY = Infinity, maxX = 0, maxY = 0;

  assetStore.techs.forEach((tech) => {
    const endElement = document.getElementById(`tech-${tech.code}`)

    if (!endElement) {
      logger.warn(`End element not found for tech: ${tech.code}`)
      return
    }

    tech.requirements.forEach((requirement: { code: string; type: string }) => {
      const startElement = document.getElementById(`tech-${requirement.code}`)

      if (!startElement) {
        logger.warn(`Start element not found for requirement: ${requirement.code}`)
        return
      }

      const startBox = startElement.getBoundingClientRect()
      const endBox = endElement.getBoundingClientRect()

      minX = Math.min(minX, startBox.left, endBox.left)
      minY = Math.min(minY, startBox.top, endBox.top)
      maxX = Math.max(maxX, startBox.right, endBox.right)
      maxY = Math.max(maxY, startBox.bottom, endBox.bottom)
    })
  })

  const offsetX = 0;   // Move viewBox 50px left
  const offsetY = 50;  // Move viewBox 100px up (so content appears lower)
  const padding = 50;   // Add extra space all around

  const width = maxX - minX + 200;
  const height = maxY - minY;

// Expand the total width/height by twice the padding (on each side: left/right, top/bottom).
  const expandedWidth = width + padding * 2;
  const expandedHeight = height + padding * 2;

  svgContainer.value
    .size(expandedWidth, expandedHeight)
    // Shift the viewBox by offsetX / offsetY, plus incorporate padding
    .viewbox(
      (minX - padding) - offsetX,
      (minY - padding) - offsetY,
      expandedWidth,
      expandedHeight
    );

  // Create an arrow marker
  const marker = svgContainer.value.marker(5, 5, (add) => {
    add.path("M0,0 L0,6 L9,3 z").fill("#334155");
  }).ref(6, 3).viewbox(0, 0, 10, 10).orient('auto-start-reverse');

  assetStore.techs.forEach((tech) => {
    const endElement = document.getElementById(`tech-${tech.code}`)

    if (!endElement) {
      logger.warn(`End element not found for tech: ${tech.code}`)
      return
    }

    tech.requirements.forEach((requirement: { code: string; type: string }) => {
      const startElement = document.getElementById(`tech-${requirement.code}`)

      if (!startElement) {
        logger.warn(`Start element not found for requirement: ${requirement.code}`)
        return
      }

    const startBox = startElement.getBoundingClientRect();
    const endBox = endElement.getBoundingClientRect();

    // Check where the line should come out
    // If the endx is higher than startx, then use the right of the box
    let startBoxX = 0;
    let startBoxY = 0;
    let controlPoint1X = 0;
    let controlPoint1Y = 0;
    const offset = 5; // Offset to prevent arrow overlap

    if(startBox.left < endBox.left) {
      startBoxX = (startBox.left - parentBox.left) + startBox.width;
      startBoxY = (startBox.top - parentBox.top) + startBox.height / 2;
      controlPoint1X = startBoxX + 50;
      controlPoint1Y = startBoxY;
    } else if(startBox.left === endBox.left && startBox.top > endBox.top) {
      // Arrow UP
      startBoxX = (startBox.left - parentBox.left) + startBox.width /2;
      startBoxY = (startBox.top - parentBox.top);
      controlPoint1X = startBoxX;
      controlPoint1Y = startBoxY - 50;
    } else if(startBox.left === endBox.left && startBox.top < endBox.top) {
      // Arrow DOWN
      startBoxX = (startBox.left - parentBox.left) + startBox.width/2;
      startBoxY = (startBox.top - parentBox.top) + startBox.height;
      controlPoint1X = startBoxX;
      controlPoint1Y = startBoxY + 50;

    }

    let endBoxX = 0;
    let endBoxY = 0;
    let controlPoint2X = 0;
    let controlPoint2Y = 0;

    if (startBox.left < endBox.left) {
      endBoxX = (endBox.left - parentBox.left) - offset;
      endBoxY = (endBox.top - parentBox.top) + endBox.height / 2;
      controlPoint2X = endBoxX - 150;
      controlPoint2Y = endBoxY;
    } else if(startBox.left === endBox.left && startBox.top > endBox.top) {
      // Arrow UP
      endBoxX = (endBox.left - parentBox.left) + endBox.width / 2;
      endBoxY = (endBox.top - parentBox.top) + offset + (endBox.height);
      controlPoint2X = endBoxX;
      controlPoint2Y = endBoxY + 50;
    } else if(startBox.left === endBox.left && startBox.top < endBox.top) {
      // Arrow Down
      endBoxX = (endBox.left - parentBox.left) + endBox.width / 2;
      endBoxY = (endBox.top - parentBox.top) - offset;
      controlPoint2X = endBoxX;
      controlPoint2Y = endBoxY - 50;
    }

    const curve = new Bezier([
      { x: startBoxX, y: startBoxY },
      { x: controlPoint1X, y: controlPoint1Y }, // Control point 1
      { x: controlPoint2X, y: controlPoint2Y }, // Control point 2
      { x: endBoxX, y: endBoxY }
    ]);

    const svgPath = curve.toSVG();
    const line = svgContainer.value!.path(svgPath).stroke({ color: '#334155', width: 4 }).fill('none');

    // Attach the marker to the end of the line
    line.marker('end', marker);

  });

});
}

function focusOnCurrentResearch() {
  let currentTech = assetStore.findTechById(gameStore.player?.currentTechId ?? 0)
  if (!currentTech) {
    currentTech = assetStore.findTechById(1)
  }

  if (!currentTech) {
    logger.warn('Current tech not found')
    return
  }
  const targetElement = document.querySelector(`#tech-` + currentTech.code) as HTMLElement;
  if (targetElement) {
    const parentElement = containerElement;
    if(!parentElement) {
      logger.warn('Parent element not found');
      return;
    }

    // Calculate the center position accounting for zoom
    const parentCenterX = parentElement.clientWidth / 2;
    const parentCenterY = parentElement.clientHeight / 2;
    const targetCenterX = (targetElement.offsetLeft + (targetElement.clientWidth / 2)) * zoomLevel.value;
    const targetCenterY = (targetElement.offsetTop + (targetElement.clientHeight / 2)) * zoomLevel.value;

    const scrollLeft = targetCenterX - parentCenterX;
    const scrollTop = targetCenterY - parentCenterY;

    // Scroll to the calculated position
    parentElement.scrollTo({
      top: scrollTop,
      left: scrollLeft,
    });
  } else {
    logger.warn('Target element not found: #tech-particle_physics');
  }
}

// Modal actions
function openLegendModal() {
  gameStore.modalType = 'techLegend'
}

function openQueueModal() {
  gameStore.modalType = 'techQueue';
}

function openAllianceModal() {
  gameStore.modalType = 'allianceSettings'
}

// Lifecycle hooks
onMounted(async () => {
  await nextTick(); // Ensure the DOM is fully rendered

  // Assign the container element
  containerElement = document.querySelector('.tech-parent');
  contentElement = document.querySelector('.tech-content');
  if (containerElement) {
    containerElement.addEventListener('mousedown', onMouseDown);
    containerElement.addEventListener('mousemove', onMouseMove);
    containerElement.addEventListener('mouseup', onMouseUp);
    containerElement.addEventListener('mouseleave', onMouseUp);
    containerElement.addEventListener('wheel', onWheel, { passive: false });

    if (contentElement) {
      svgContainer.value = SVG().addTo(contentElement).size('100%', '100%');
      
      // Set up resize observer to recalculate scroll limits
      resizeObserver = new ResizeObserver(() => {
        limitScroll(containerElement!.scrollLeft, containerElement!.scrollTop);
      });
      resizeObserver.observe(containerElement);
      resizeObserver.observe(contentElement);
    }
  }

  drawLines();

  focusOnCurrentResearch()
  
  // Apply scroll limits after initial render
  await nextTick();
  limitScroll(containerElement?.scrollLeft, containerElement?.scrollTop);

})

onUnmounted(() => {
  // Reset alliance prototype selection flag when leaving research page
  if (gameStore.alliancePrototypeSelection) {
    gameStore.resetAlliancePrototypeSelection()
  }

  if (containerElement) {
    containerElement.removeEventListener('mousedown', onMouseDown)
    containerElement.removeEventListener('mousemove', onMouseMove)
    containerElement.removeEventListener('mouseup', onMouseUp)
    containerElement.removeEventListener('mouseleave', onMouseUp)
    containerElement.removeEventListener('wheel', onWheel)
  }

  if (svgContainer.value) {
    svgContainer.value.clear()
    svgContainer.value.remove()
  }
  
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
})
</script>

<template>
  <div class="relative w-full" @contextmenu.prevent>
    <div v-if="gameStore.alliancePrototypeSelection" class="absolute top-2 left-1/2 transform -translate-x-1/2 bg-slate-700 text-slate-400 px-4 py-2 rounded-lg shadow-lg z-20">
      <div class="text-center">
        <div class="font-semibold">Alliance Prototype Selection</div>
        <div class="text-sm">Select a technology to research a prototype for</div>
      </div>
    </div>
    <div class="absolute top-2 right-2 flex gap-2 z-10">
      <ActionButton v-if="gameStore.player?.allianceId && !gameStore.alliancePrototypeSelection" @click="openAllianceModal">
        Alliance: {{ gameStore.player?.allianceSciencePointsAllocationPercentage ?? 0 }}%
      </ActionButton>
      <ActionButton @click="openLegendModal">Legend</ActionButton>
      <ActionButton v-if="!gameStore.alliancePrototypeSelection" @click="openQueueModal">Queue</ActionButton>
    </div>
    <div class="tech-parent content-height-limit cursor-grab select-none" >
      <div class="tech-content" :style="{ transform: `scale(${zoomLevel})`, transformOrigin: '0 0' }">
        <template v-for="tech in assetStore.techs.values()" :key="tech.code">
          <PrototypeItem v-if="gameStore.alliancePrototypeSelection" :id="'tech-' + tech.code" :tech="tech" />
          <TechnologyItem v-else :id="'tech-' + tech.code" :tech="tech" />
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tech-parent {
  position: relative;
  width: 100%;
  overflow: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  touch-action: pan-x pan-y; /* Allow native touch scrolling on mobile */
}

.tech-parent::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

.tech-content {
  position: relative;
  width: max-content;
  height: max-content;
  transition: none;
  padding-right: 50px;
  padding-bottom: 50px;
}

.grabbing {
  cursor: grabbing !important;
}

.hide-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
</style>
